import React from 'react'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import {useNavigate} from 'react-router-dom'

interface Props {
  openAppointments: number
  confirmedAppointments: number
  interpretationServedMinutes: number
  completionRate: number
  periodLabel: string
}

export const ClientSummaryCards: React.FC<Props> = ({
  openAppointments,
  confirmedAppointments,
  interpretationServedMinutes,
  completionRate,
  periodLabel,
}) => {
  const navigate = useNavigate()

  const formatDate = (date: Date): string => {
    const year = date.getFullYear()
    const month = `${date.getMonth() + 1}`.padStart(2, '0')
    const day = `${date.getDate()}`.padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const handleViewAssignments = () => {
    navigate('/appointment/2?status=0,1,3')
  }

  const handleViewAppointments = () => {
    const today = new Date()
    const endDate = new Date()
    endDate.setDate(today.getDate() + 30)
    navigate(`/appointment/2?status=2&startDate=${formatDate(today)}&endDate=${formatDate(endDate)}`)
  }

  const formatMinutes = (mins: number): string => {
    if (mins >= 1000) return mins.toLocaleString()
    return mins.toString()
  }

  const renderTitle = (label: string, tooltip: string) => (
    <span className='cp-summary-card__title'>
      {label}
      <OverlayTrigger
        placement='top'
        overlay={
          <Tooltip
            id={`client-summary-${label.replace(/\s+/g, '-').toLowerCase()}`}
            className='client-portal-tooltip'
          >
            {tooltip}
          </Tooltip>
        }
      >
        <span className='cp-info-icon' tabIndex={0}>
          i
        </span>
      </OverlayTrigger>
    </span>
  )

  return (
    <div className='cp-summary-row'>
      <div className='cp-summary-card cp-summary-card--olive'>
        {renderTitle('Open Appointments', 'Count of open jobs that have been created but not yet confirmed or assigned.')}
        <div style={{flex: 1}}>
          <div className='cp-summary-card__value'>{openAppointments}</div>
          <div className='cp-summary-card__subtext'>All Open Jobs</div>
        </div>
        <div className='cp-summary-card__btn'>
          <button type='button' onClick={handleViewAssignments}>
            View Assignments
          </button>
        </div>
      </div>

      <div className='cp-summary-card cp-summary-card--green'>
        {renderTitle('Confirmed', 'Count of confirmed appointments scheduled within the next 30 days.')}
        <div style={{flex: 1}}>
          <div className='cp-summary-card__value'>{confirmedAppointments}</div>
          <div className='cp-summary-card__subtext'>Next 30 days scheduled</div>
        </div>
        <div className='cp-summary-card__btn'>
          <button type='button' onClick={handleViewAppointments}>
            View Appointments
          </button>
        </div>
      </div>

      <div className='cp-summary-card cp-summary-card--red'>
        {renderTitle('Interpretation Served', `Total interpretation minutes delivered in the selected period (${periodLabel}) across OPI, IPI, and VIS.`)}
        <div style={{flex: 1}}>
          <div className='cp-summary-card__value'>{formatMinutes(interpretationServedMinutes)}</div>
          <div className='cp-summary-card__subtext'>{periodLabel}</div>
          <p className='cp-summary-card__desc'>This includes all services OPI, IPI, VIS</p>
        </div>
      </div>

      <div className='cp-summary-card cp-summary-card--accent'>
        {renderTitle('Completion Rate', `Completed appointments divided by total scheduled appointments across all service types over the selected period (${periodLabel}), multiplied by 100.`)}
        <div style={{flex: 1}}>
          <div className='cp-summary-card__value'>{completionRate}%</div>
          <div className='cp-summary-card__subtext'>{periodLabel}</div>
          <p className='cp-summary-card__desc'>This includes all appointments</p>
        </div>
      </div>
    </div>
  )
}
